<script lang="ts">
	import { Button, ButtonType, Dropdown, OverlayScrollbar } from '@repo/commons/components'
	import type { DropdownItem, SidebarSession } from '@repo/commons/models'
	import SessionItem from './sessionItem/SessionItem.svelte'

	interface Props {
		sessions: SidebarSession[]
		hasMore: boolean
		activeSessionId?: string | undefined
		isLoadingSessions?: boolean
		isRefetchingSessions?: boolean
		isLoadingMore?: boolean
		isError?: boolean
		errorMessage?: string
		showHeaderActionsButton?: boolean
		showNewChatButton?: boolean
		onClick: (sessionId: string) => void
		onLoadMore: () => void
		onDelete?: (sessionId: string) => void
	}

	let {
		sessions,
		activeSessionId,
		hasMore,
		isLoadingSessions = false,
		isRefetchingSessions = false,
		isLoadingMore = false,
		isError = false,
		errorMessage = 'Failed to load sessions',
		showHeaderActionsButton = true,
		showNewChatButton = false,
		onClick,
		onLoadMore,
		onDelete
	}: Props = $props()

	const showLoadMoreButton = $derived.by<boolean>(() => {
		return !!(hasMore && sessions.length)
	})

	const showInitialLoading = $derived(isLoadingSessions && !sessions.length)
	const showListAsTransparent = $derived(
		isRefetchingSessions || (isLoadingSessions && !!sessions.length)
	)

	const handleItemSelect = (item: DropdownItem) => {
		console.log('Selected item:', item)
	}

	const handleNewChat = () => {
		console.log('New chat')
	}
</script>

<div class="sidebar-wrapper">
	<div class="sidebar-heading">
		<div class="heading-container">
			<p class="text-base font-semibold text-gray-900">Sessions</p>
		</div>
		{#if showNewChatButton}
			<div class="pt-4">
				<Button
					buttonText="New chat"
					buttonType={ButtonType.BRAND}
					onclick={handleNewChat}
					iconProps={{ name: 'plus', class: 'text-white' }}
					fillWidth={true}
				/>
			</div>
		{/if}
	</div>

	<OverlayScrollbar class="sidebar-scroll">
		{#if showInitialLoading}
			<p class="py-8 text-center text-sm text-[rgba(0,29,51,0.65)]">Loading...</p>
		{:else if sessions.length}
			<div class="session-list" class:session-list--loading={showListAsTransparent}>
				{#each sessions as session}
					<SessionItem
						id={session.id}
						startTime={session.startTime}
						modality={session.modality}
						isActive={activeSessionId === session.id}
						onClick={() => onClick(session.id)}
						onDelete={onDelete ? () => onDelete(session.id) : undefined}
					/>
				{/each}
			</div>
			{#if showLoadMoreButton}
				<div class="load-more-wrapper">
					<Button
						iconProps={{ name: 'chevronDown' }}
						buttonType={ButtonType.TERTIARY}
						buttonText={isLoadingMore ? 'Loading...' : 'Load More'}
						disabled={isLoadingMore}
						onclick={onLoadMore}
					/>
				</div>
			{/if}
		{:else if isError}
			<p class="error-state">{errorMessage}</p>
		{:else}
			<p class="empty-state">No sessions found</p>
		{/if}
	</OverlayScrollbar>
</div>

<style lang="postcss">
	.sidebar-wrapper {
		@apply h-full flex flex-col;
	}

	.sidebar-heading {
		@apply flex flex-col p-4 text-base font-semibold text-gray-900 flex-shrink-0 gap-4;
	}

	.heading-container {
		@apply flex justify-between items-center;
	}

	:global(.sidebar-scroll) {
		@apply flex-1 min-h-0 px-4;
	}

	.session-list {
		@apply flex flex-col gap-1 transition-opacity duration-200;
	}

	.session-list--loading {
		@apply opacity-40 pointer-events-none;
	}

	.load-more-wrapper {
		@apply flex justify-center items-center py-4;
	}

	.empty-state {
		@apply py-8 text-center text-sm text-[rgba(0,29,51,0.65)];
	}

	.error-state {
		@apply py-8 text-center text-sm text-[rgba(0,29,51,0.65)];
	}
</style>
